import type { StatusBadgeTone } from "~/components/ui/status-badge";

// AIDEV-NOTE: PC-library content dimensions (Aug 2026 experiment). Values are validated
// here — the DB columns are plain nullable text. Clinical-team resources predate the
// taxonomy and carry nulls: they get no badges and full visibility.

export type TResourceAudience = "client" | "practitioner" | "both";
export type TResourceLevel = "foundations" | "deepening" | "advanced";

export const RESOURCE_LEVELS: TResourceLevel[] = [
    "foundations",
    "deepening",
    "advanced",
];

export function isResourceLevel(v: unknown): v is TResourceLevel {
    return RESOURCE_LEVELS.includes(v as TResourceLevel);
}

export function isResourceAudience(v: unknown): v is TResourceAudience {
    return v === "client" || v === "practitioner" || v === "both";
}

// AIDEV-NOTE: Level → StatusBadge tone. Existing palette tones only (never invent
// pairings): the foundations→deepening→advanced progression reads info → neutral →
// success. Audience gets a badge ONLY for practitioner-only content ("For practitioners",
// warn tone reads as amber attention, wrong here — use info) shown to practitioners;
// clients never see practitioner-only resources at all, so no badge state exists for them.
export const LEVEL_BADGE: Record<
    TResourceLevel,
    { tone: StatusBadgeTone; label: string }
> = {
    foundations: { tone: "info", label: "Foundations" },
    deepening: { tone: "neutral", label: "Deepening" },
    advanced: { tone: "success", label: "Advanced" },
};

export const PRACTITIONER_BADGE: { tone: StatusBadgeTone; label: string } = {
    tone: "info",
    label: "For practitioners",
};

/** Sort key: foundations first, unleveled (clinical legacy) content keeps its place ahead. */
export function levelRank(level: string | null): number {
    const idx = RESOURCE_LEVELS.indexOf(level as TResourceLevel);
    return idx === -1 ? -1 : idx;
}

/** A client may see a resource unless it is explicitly practitioner-only. */
export function isClientVisible(audience: string | null): boolean {
    return audience !== "practitioner";
}

// AIDEV-NOTE: show-pc-library flag (fail-closed) swaps two mutually exclusive worlds:
// OFF = prod-as-today (Getting Started visible, library hidden); ON = library visible,
// Getting Started hidden. Gate at the collection grain in loaders — resources inherit
// visibility from their collection, and deep links 404 via getCollectionBySlug callers.
export const SHOW_PC_LIBRARY_FLAG = "show-pc-library";
export const LIBRARY_CATEGORY = "library";
const RETIRED_BY_LIBRARY_SLUGS = ["getting-started"];

// AIDEV-NOTE: hwa-launch flag (fail-closed) is a release kill-switch, NOT an entitlement:
// it exists so Parts Companion can ship to production at any time without also publishing
// Tamala Floyd's Healing Works Academy course. Keyed on the collection CATEGORY, which every
// HWA collection already carries, so a new HWA collection is covered the moment it is seeded
// rather than needing to be added to a list here. Seeded globally disabled with an empty
// "HWA Launch" segment; staging links the All Clients + All Practitioners system segments.
export const HWA_LAUNCH_FLAG = "hwa-launch";
export const HWA_CATEGORY = "healing-works-academy";

/** The per-viewer flag state the resources area needs to decide what is visible. */
export interface ResourceVisibilityFlags {
    hasLibraryFlag: boolean;
    hasHwaFlag: boolean;
}

export function isCollectionVisibleWithFlag(
    collection: { category: string | null; slug: string },
    { hasLibraryFlag, hasHwaFlag }: ResourceVisibilityFlags,
): boolean {
    if (collection.category === HWA_CATEGORY) return hasHwaFlag;
    if (collection.category === LIBRARY_CATEGORY) return hasLibraryFlag;
    if (RETIRED_BY_LIBRARY_SLUGS.includes(collection.slug))
        return !hasLibraryFlag;
    return true;
}

/**
 * Every gate a resource page must pass before it renders — or signs a video token.
 *
 * AIDEV-NOTE: Extracted so the rule is one testable thing rather than a chain of early
 * returns inside a loader. The loader cannot delegate this to the collection layout's 404:
 * Remix runs parent and child loaders in PARALLEL, so the parent throwing never stopped the
 * child from minting a signed Cloudflare playback URL and returning it. All three checks are
 * load-bearing:
 *   - `belongsToCollection` — without it, ANY visible collection path reaches a gated
 *     collection's resource by slug, which is how paid HWA video would have leaked;
 *   - `collectionVisible` — the show-pc-library swap AND the hwa-launch kill-switch,
 *     applied to the resource's OWN collection rather than whichever one the URL named.
 *     This is the check that stops gated HWA video being signed, so it is load-bearing
 *     for the release switch and not merely cosmetic;
 *   - `audienceAllows` — the layout's audience gate only filters the sidebar LIST, so a
 *     client could otherwise deep-link a practitioner-only resource.
 */
export function canViewResourcePage({
    resource,
    collection,
    flags,
    viewerIsPractitioner,
}: {
    resource: { primaryCollectionId: string | null; audience: string | null };
    collection: { id: string; category: string | null; slug: string };
    flags: ResourceVisibilityFlags;
    viewerIsPractitioner: boolean;
}): boolean {
    const belongsToCollection = resource.primaryCollectionId === collection.id;
    const collectionVisible = isCollectionVisibleWithFlag(collection, flags);
    const audienceAllows =
        viewerIsPractitioner || isClientVisible(resource.audience);
    return belongsToCollection && collectionVisible && audienceAllows;
}
